<template>
    <ProductProperties
        :data="{
            title: 'Accent gems',
            sections,
        }"
    />
</template>

<script lang="ts">
const join = (a: string[]) => a.join(' • ')
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { unique } from 'remeda'

import type { AccentGemFragment } from '@/graphql/api'

import ProductProperties, {
    type ProductPropertiesSection,
} from './ProductProperties.vue'

const { accentGems } = defineProps<{
    accentGems: AccentGemFragment[]
}>()

const sections = computed<ProductPropertiesSection[]>(() => {
    const gemtTypeList = unique(
        accentGems?.map((gem) => gem.melee.crystal.translation.name) || []
    )

    const gemTypes: ProductPropertiesSection = {
        heading: 'Gem types',
        definition: join(gemtTypeList),
    }

    const colorAndClarity: ProductPropertiesSection[] = gemtTypeList
        .reduce(
            (acc, gemType) => {
                const uniqueGems = accentGems?.filter(
                    (gem) => gem.melee.crystal.translation.name === gemType
                )

                return [
                    ...acc,
                    {
                        heading: gemType,
                        color: unique(
                            uniqueGems
                                ?.map((gem) =>
                                    gem.melee.colors.map(
                                        (color) => color.translation.name
                                    )
                                )
                                .flat() || []
                        ),
                        clarity: unique(
                            uniqueGems
                                ?.map((gem) =>
                                    gem.melee.clarities.map(
                                        (clarity) => clarity.translation.name
                                    )
                                )
                                .flat() || []
                        ),
                    },
                ]
            },
            [] as {
                heading: string
                color: string[]
                clarity: string[]
            }[]
        )
        .map((gem) => [
            {
                heading: `${gem.heading} colour`,
                definition: join(gem.color),
            },
            {
                heading: `${gem.heading} clarity`,
                definition: join(gem.clarity),
            },
        ])
        .flat()

    const totalCaratWeight: ProductPropertiesSection = {
        heading: 'Estimated total carat weight',
        definition:
            accentGems
                ?.reduce(
                    (total, item) =>
                        total + item.quantity * Number(item.melee.caratWeight),
                    0
                )
                .toFixed(2) + 'ct',
    }

    return [gemTypes, ...colorAndClarity, totalCaratWeight]
})
</script>
